import type { FetchResult, Operation } from '@apollo/client';

export type GqlOperationResponse = FetchResult & {
  errors?: ReadonlyArray<unknown>;
};

export type SuccessfulMutationMetadata = Readonly<
  Record<string, string | number | boolean | string[]>
>;

export type SuccessfulGqlMutationCallbackArgs = {
  operationName: string;
  mutationName: string;
  variables: Operation['variables'];
  response: GqlOperationResponse;
  metadata?: Readonly<Record<string, unknown>>;
};

export type SuccessfulGqlMutationCallback = (
  args: SuccessfulGqlMutationCallbackArgs
) => void;

export const skipSuccessfulMutationTrackingContext = {
  skipSuccessfulMutationTracking: true,
} as const;

export function successfulMutationMetadataContext(
  metadata: SuccessfulMutationMetadata
) {
  return { successfulMutationMetadata: metadata };
}

function recordFrom(value: unknown): Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null
    ? Object.fromEntries(Object.entries(value))
    : {};
}

export function notifySuccessfulGqlMutations({
  operation,
  response,
  onSuccessfulMutation,
}: {
  operation: Operation;
  response: GqlOperationResponse;
  onSuccessfulMutation?: SuccessfulGqlMutationCallback;
}) {
  const context = operation.getContext();
  if (
    !onSuccessfulMutation ||
    response.errors?.length ||
    context.skipSuccessfulMutationTracking === true
  ) {
    return;
  }

  const mutationNames = getMutationNamesFromOperation(operation);
  const metadata = recordFrom(context.successfulMutationMetadata);

  mutationNames.forEach((mutationName) => {
    try {
      onSuccessfulMutation({
        operationName: operation.operationName,
        mutationName,
        variables: operation.variables,
        response,
        metadata,
      });
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          '[GQLApiClient] Failed to handle successful mutation',
          mutationName,
          error
        );
      }
    }
  });
}

export function getMutationNamesFromOperation(operation: Operation) {
  const mutationDefinition = operation.query.definitions.find((definition) => {
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'mutation'
    );
  });

  if (
    !mutationDefinition ||
    mutationDefinition.kind !== 'OperationDefinition'
  ) {
    return [];
  }

  return mutationDefinition.selectionSet.selections.reduce<Array<string>>(
    (mutationNames, selection) => {
      if (selection.kind === 'Field' && selection.name.value !== '__typename') {
        mutationNames.push(selection.name.value);
      }

      return mutationNames;
    },
    []
  );
}
